// Ported from dev-scrips-pm-react src/billing/components/service-picker-dialog.tsx (PROD-3312 appointment Billing tab).
import { useState } from 'react';
import { Segmented, TextField } from '@scripsteam/scrips-signal-ds';
import { Search } from 'lucide-react';
import {
  BridgeButton as Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@/billing/bridge';
import { isEmptyGuid } from '@/shared/lib/empty-guid';
import {
  canFetchServiceFees,
  useServiceFees,
  type ServiceFeesType,
} from '@/billing/services/use-invoice-services';

/**
 * Add-service picker for the Draft billing invoice (Flutter `AddProcedureBillingCodeWidget`). Lists the provider's
 * billable services (`useServiceFees` -> POST /Billing/Services/Fees); picking one calls `onSelect(serviceId)` and
 * closes. PA differences, all primitives: the Provider Fees / All switch is the Signal DS `Segmented` (pm-react
 * hand-rolls two buttons; the DS control is a radio group with no per-option tooltip, so the "no provider" reason is
 * the line of text under it), and the list scroll cap is the named `max-h-96` (pm-react `max-h-[50vh]`).
 */
interface ServicePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId?: string;
  locationId?: string;
  patientId?: string;
  currency?: string;
  /** Service ids already on the invoice — shown as "Added" + non-selectable. */
  existingServiceIds?: string[];
  /** Seeds the search box (PROD-2678: a NoPrice order opens pre-filtered by its code).
   *  Read once on mount; remount (`key`) to seed a different value. */
  initialQuery?: string;
  onSelect: (serviceId: string) => void;
}

function feeFor(locationId?: string, fees?: { fee?: number; location?: { id?: string } }[]): number | undefined {
  if (!fees || fees.length === 0) return undefined;
  const match = locationId ? fees.find((f) => f.location?.id === locationId) : undefined;
  return (match ?? fees[0])?.fee;
}

/**
 * HTTP status of a failed fee request, if the failure carries one. Duck-typed rather than `instanceof ApiError`: the
 * error arrives through React Query as `unknown`, and a substituted api-client (a test) has a different class identity.
 */
function httpStatusOf(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

export function ServicePickerDialog({
  open,
  onOpenChange,
  providerId,
  locationId,
  patientId,
  currency = 'AED',
  existingServiceIds = [],
  initialQuery = '',
  onSelect,
}: ServicePickerDialogProps) {
  const [query, setQuery] = useState(initialQuery);
  // Search is server-side (`&q=` on /Services/Fees) on EVERY keystroke, no debounce (Andrew 2026-08-25, as pm-react).
  const [preferredType, setPreferredType] = useState<ServiceFeesType>('provider');

  // PROD-2368. A device booking has NO provider (`''` or `Guid.Empty`), and Provider Fees is then a rejected request
  // (400 "ProviderId cannot be empty when Type is Provider"), so the effective type is DERIVED, not initialised.
  const hasProvider = !isEmptyGuid(providerId);
  const type: ServiceFeesType = hasProvider ? preferredType : 'all';

  const feesQuery = useServiceFees({ providerId, locationId, patientId, type, query }, open);

  const results = feesQuery.data?.results ?? [];
  // The request the hook declines to make (today: no patient). A request that never happened must not render as a
  // catalogue that came back empty.
  const willFetch = canFetchServiceFees({ providerId, patientId, type });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add service</DialogTitle>
          <DialogDescription>Pick a billable service to add to this invoice.</DialogDescription>
        </DialogHeader>

        {/* Provider Fees vs All (Flutter type: "provider" | "all"). */}
        <Segmented<ServiceFeesType>
          aria-label="Service list"
          variant="pill"
          className="self-start"
          options={[
            { value: 'provider', label: 'Provider Fees', disabled: !hasProvider },
            { value: 'all', label: 'All' },
          ]}
          value={type}
          onChange={setPreferredType}
        />

        {!hasProvider && (
          <p className="text-xs text-text-muted">
            This invoice has no provider, so only the full catalogue is available.
          </p>
        )}

        <TextField
          type="text"
          size="compact"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or code"
          leadingIcon={<Search className="h-4 w-4 text-text-muted" aria-hidden />}
        />

        <div className="max-h-96 overflow-y-auto scrollbar-hide -mx-1 px-1">
          {feesQuery.isLoading ? (
            <div className="space-y-2 py-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : feesQuery.isError ? (
            /* PROD-2368: a FAILED request is never an empty state. */
            <div role="alert" className="py-6 px-2 text-center space-y-2">
              <p className="text-sm text-status-error">Couldn&apos;t load services.</p>
              <p className="text-xs text-text-muted">
                {httpStatusOf(feesQuery.error) != null
                  ? `The request was rejected (HTTP ${httpStatusOf(feesQuery.error)}).`
                  : 'The service catalogue could not be reached.'}{' '}
                This is not the same as an empty catalogue.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void feesQuery.refetch();
                }}
              >
                Retry
              </Button>
            </div>
          ) : !willFetch ? (
            <p className="text-sm text-text-muted py-6 text-center" role="status">
              This invoice has no patient yet, so its services can&apos;t be listed.
            </p>
          ) : results.length === 0 ? (
            <p className="text-sm text-text-muted py-6 text-center">No services found.</p>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {results.map((r) => {
                const id = r.id ?? '';
                const added = existingServiceIds.includes(id);
                const fee = feeFor(locationId, r.fees);
                return (
                  <li key={id}>
                    <button
                      type="button"
                      disabled={added || !id}
                      onClick={() => {
                        onSelect(id);
                        onOpenChange(false);
                      }}
                      className="w-full flex items-center justify-between gap-4 py-2.5 px-2 text-left hover:bg-grey-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm text-text-primary truncate">
                          {r.procedureCode?.displayName ?? r.shortDescription ?? '—'}
                        </span>
                        <span className="block text-xs text-text-muted">
                          {r.procedureCode?.code} · {r.procedureCode?.system}
                        </span>
                      </span>
                      <span className="shrink-0 text-sm tabular-nums text-text-secondary">
                        {added ? 'Added' : fee != null ? `${currency} ${fee}` : ''}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
